import { Button, cn, OdinIcon, Spinner } from '@myosh/odin-components';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../redux/hooks';
import { JobStatus, removeJob, type SubmittedJobDetails } from '../../redux/slices/running-jobs';

interface RunningJobsPanelProps {
  submittedJobs: SubmittedJobDetails[];
}

const RunningJobsPanel: React.FC<RunningJobsPanelProps> = ({ submittedJobs }) => {
  const panelButtonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [hasViewedJobs, setHasViewedJobs] = useState(false);

  const onPanelButtonClick = () => setIsPanelVisible(!isPanelVisible);
  const onCloseButtonClick = () => setIsPanelVisible(false);

  const getPanelButtonStatusIcon = () => {
    const statusIconClasses = 'size-4 absolute -top-2 -right-2';
    const latestJobStatus = submittedJobs[0].status;

    if (submittedJobs.some((job: SubmittedJobDetails) => job.status === JobStatus.InProgress)) {
      return <Spinner className={statusIconClasses} />;
    } else if (hasViewedJobs) {
      return null;
    } else if (latestJobStatus === JobStatus.Failed) {
      return <OdinIcon icon="CircleAlert" className={cn(statusIconClasses, 'text-error')} />;
    } else if (latestJobStatus === JobStatus.Warning) {
      return <OdinIcon icon="CircleAlert" className={cn(statusIconClasses, 'text-warning')} />;
    } else if (latestJobStatus === JobStatus.Success) {
      return <OdinIcon icon="CircleCheck" className={cn(statusIconClasses, 'text-success')} />;
    } else {
      return null;
    }
  };

  const onJobClick = (job: SubmittedJobDetails) => {
    if (job && job.url && job.name && (job.status === JobStatus.Success || job.status === JobStatus.Warning)) {
      window.open(job.url);
    }
  };

  const onJobRemove = (id: number) => {
    if (id) {
      dispatch(removeJob(id));
    }
  };

  const getStatusIcon = (status?: JobStatus) => {
    if (status) {
      if (status === JobStatus.InProgress) {
        return <Spinner />;
      } else if (status === JobStatus.Success) {
        return <OdinIcon icon="CircleCheck" className="size-4 text-success" />;
      } else if (status === JobStatus.Warning) {
        return <OdinIcon icon="CircleAlert" className="size-4 text-warning" />;
      } else if (status === JobStatus.Failed) {
        return <OdinIcon icon="CircleAlert" className="size-4 text-error" />;
      }
    }
  };

  useEffect(() => {
    if (isPanelVisible) {
      if (submittedJobs.every((job: SubmittedJobDetails) => job.status !== JobStatus.InProgress)) {
        setHasViewedJobs(true);
      } else {
        setHasViewedJobs(false);
      }
    }

    if (submittedJobs.length === 0) {
      setIsPanelVisible(false);
    }
  }, [isPanelVisible, submittedJobs]);

  return (
    <div className="flex items-center">
      {submittedJobs && submittedJobs.length > 0 && (
        <div data-cy="running-jobs-container" className="relative">
          <Button
            variant="ghost"
            size="icon"
            data-cy="running-jobs-toggle-button"
            ref={panelButtonRef}
            aria-haspopup="dialog"
            aria-expanded={isPanelVisible}
            onClick={onPanelButtonClick}
            className="relative"
          >
            <OdinIcon icon="FolderDown" className="size-5" />
            {getPanelButtonStatusIcon()}
          </Button>
          {isPanelVisible && (
            <div
              data-cy="running-jobs-panel"
              className="bg-muted absolute top-10 z-4001 max-w-[25rem] min-w-[14rem] rounded-lg shadow-lg"
            >
              <div data-cy="running-jobs-header" className="flex items-center justify-between p-4">
                <span data-cy="running-jobs-header-title" className="whitespace-nowrap">
                  Job Status
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  data-cy="running-jobs-close-button"
                  aria-label={t(($) => $.closeRunningJobsPanel)}
                  onClick={onCloseButtonClick}
                >
                  <OdinIcon icon="X" />
                </Button>
              </div>
              <div data-cy="running-jobs-list" className="bg-popover space-y-2 rounded-b-lg p-4">
                {submittedJobs.map((job: SubmittedJobDetails) => {
                  const itemStyles = cn('flex gap-2 items-center min-w-0 w-full', {
                    'hover:cursor-pointer': job.status === JobStatus.Success || job.status === JobStatus.Warning,
                  });

                  return (
                    <div
                      key={job.id}
                      data-cy={`running-job-${job.id}`}
                      className="flex items-center gap-1 p-1 [&:has(div:hover)]:bg-accent"
                    >
                      <div
                        data-cy={`running-job-item-${job.id}`}
                        className={itemStyles}
                        onClick={() => onJobClick(job)}
                      >
                        <OdinIcon icon="FileText" className="size-5" />
                        <span className="flex-1 truncate" title={job.name}>
                          {job.name}
                        </span>
                        <span>{getStatusIcon(job.status)}</span>
                      </div>
                      <Button
                        data-cy={`running-job-remove-${job.id}`}
                        variant="ghost"
                        size="icon-xs"
                        aria-label={t(($) => $.remove)}
                        disabled={job.status === JobStatus.InProgress}
                        onClick={() => onJobRemove(job.id)}
                      >
                        <OdinIcon icon="X" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RunningJobsPanel;
